import type { QueryKey } from '@tanstack/react-query';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { queryClient } from './queryClient';

/**
 * Real-time updates for the staff portal.
 *
 * The server streams every audited change the signed-in user is allowed to see (GET /live/stream, Server-Sent
 * Events, already filtered per user: a shop admin only ever receives their own shop's events, the gate never
 * receives shop sales). This module keeps ONE stream per browser tab, however many screens or widgets use it,
 * and turns each event into a refresh of the cached screens it affects, so a bill rung up at the counter shows
 * on the shop admin's Bills page and the owner's dashboard within about a second, without a reload.
 *
 * - Reconnects on its own with growing waits (1 s, 2 s, 4 s … 30 s), straight away when the network comes back.
 * - Pauses while the tab is hidden for a while (saves phone data and server connections) and catches up when the
 *   tab is shown again.
 * - If the server has no stream (older server, a proxy that blocks it) it goes quiet: every screen keeps its own
 *   polling, exactly as before, and nothing is shown to the user except the grey dot in the top bar.
 */

// ------------------------------------------------------------------ event shape
export interface LiveEvent {
  id: string;
  action: string;
  entity: string;
  entity_id: string | null;
  outlet_id: string | null;
  actor_name: string | null;
  /** Sent by newer servers; used by the "who did it" filter when present. */
  actor_role?: string | null;
  created_at: string;
  summary: string | null;
  /** The outlet's name as the server knew it (venue-wide events have none). */
  outlet_name?: string | null;
  /** 'low_stock' when a bill or stock change left a product at or below its alert level. */
  alert?: string | null;
  /** Done by the operations simulator (UAT and demos), not by a person. */
  simulated?: boolean;
}

export type LiveStatus = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'paused' | 'unavailable';

/** Normalises one event from the stream or from /live/recent. Returns null for anything unusable. */
export function parseLiveEvent(raw: unknown): LiveEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const str = (v: unknown) => (v == null || v === '' ? null : String(v));
  const id = str(r.id);
  const action = str(r.action);
  const entity = str(r.entity);
  if (!id || !action || !entity) return null;
  return {
    id,
    action,
    entity,
    entity_id: str(r.entity_id ?? r.entityId),
    outlet_id: str(r.outlet_id ?? r.outletId),
    actor_name: str(r.actor_name ?? r.actorName),
    actor_role: str(r.actor_role ?? r.actorRole),
    created_at: str(r.created_at ?? r.createdAt) ?? new Date().toISOString(),
    summary: str(r.summary),
    outlet_name: str(r.outlet_name ?? r.outletName),
    alert: str(r.alert),
    simulated: r.simulated === true,
  };
}

// ------------------------------------------------------------------ areas (feed filters, counters, icons)
export type LiveArea = 'gate' | 'shops' | 'money' | 'activities' | 'facility' | 'people' | 'settlements' | 'supply' | 'customers' | 'admin';

export const AREA_LABEL: Record<LiveArea, string> = {
  gate: 'Gate & parking',
  shops: 'Shops',
  money: 'Money',
  activities: 'Games, turf & events',
  facility: 'Facility',
  people: 'People & HR',
  settlements: 'Settlements',
  supply: 'Supply chain',
  customers: 'Customers & cards',
  admin: 'Admin',
};

/** Which part of the venue an event belongs to (for filters and per-area counters). */
export function areaOf(ev: Pick<LiveEvent, 'entity' | 'action'>): LiveArea {
  const e = ev.entity;
  const a = ev.action;
  if (['visit', 'vehicle', 'anpr_read', 'parking_bay', 'parking_zone', 'camera', 'vehicle_type', 'reg_series_mapping', 'pricing_rule'].includes(e) || a.startsWith('parking.') || a.startsWith('visit.')) return 'gate';
  if (['sale', 'product', 'product_category'].includes(e) || a.startsWith('sale.') || a.startsWith('product')) return 'shops';
  if (
    [
      'game', 'game_play', 'game_fault', 'turf', 'turf_booking', 'event', 'event_ticket', 'event_ticket_type',
      'turf_rule', 'turf_addon', 'turf_settings', 'turf_series', 'turf_package', 'turf_package_plan',
      'kart', 'kart_rate', 'kart_session', 'kart_maintenance',
    ].includes(e)
  )
    return 'activities';
  if (['housekeeping_task', 'housekeeping_rota', 'maintenance_ticket', 'pest_control', 'quality_inspection', 'facility_asset', 'other_service', 'facility_area', 'facility_config'].includes(e) || a.startsWith('housekeeping.') || a.startsWith('facility.')) return 'facility';
  if (['attendance', 'employee', 'payroll_run', 'payslip', 'hr_reminder', 'hr_settings', 'accommodation_room', 'accommodation_stay'].includes(e)) return 'people';
  if (['settlement', 'settlement_settings', 'shop_agreement', 'shop_closure', 'advertisement', 'cleaning_charge', 'generator_run'].includes(e)) return 'settlements';
  if (['material', 'materials', 'material_request', 'purchase_order', 'supplier', 'supplier_bill'].includes(e)) return 'supply';
  if (['payment', 'finance_entry', 'daily_closing', 'account_group'].includes(e) || a.startsWith('payment.') || a.startsWith('closing.')) return 'money';
  if (['customer', 'customer_card', 'card_settings', 'feedback', 'coupon', 'campaign', 'notification', 'terms_document'].includes(e) || a.startsWith('wallet.') || a.startsWith('card.')) return 'customers';
  return 'admin';
}

// ------------------------------------------------------------------ which cached screens an event changes
const DASH: QueryKey = ['admin', 'dashboard'];
const CARDS: QueryKey[] = [['cards-register'], ['cards-summary'], ['cards-recharges'], ['card-counter'], ['customer-wallet'], ['wallet-statement']];
const MONEY: QueryKey[] = [['payments'], ['payment'], ['closing-preview'], ['closings'], DASH, ['admin', 'finance-totals'], ['admin', 'report']];

const BY_AREA: Record<LiveArea, QueryKey[]> = {
  // Gate board, visit lists and detail, parking map/live status/visitors, the cashier desk, vehicle pages.
  gate: [['board'], ['visits'], ['visit'], ['parking'], ['vehicle'], ['customer-visits'], ['anpr-reads'], DASH],
  // Bills, bill detail, shop home / P&L / all-shops overview, POS and catalogue stock, the day's closing.
  shops: [['bills'], ['bill'], ['shop'], ['pos', 'products'], ['catalog'], ['closing-preview'], ['closings'], DASH, ['admin', 'report']],
  money: [...MONEY, ['admin', 'finance-entries'], ['board'], ['visit'], ['shop'], ...CARDS],
  activities: [['activities'], DASH],
  facility: [['facility']],
  people: [['people']],
  settlements: [['settlements']],
  supply: [['supply']],
  customers: [['customers'], ['customer'], ['feedback'], ...CARDS],
  admin: [],
};

/** Extra screens for particular records, on top of their area's list. */
const BY_ENTITY: Record<string, QueryKey[]> = {
  sale: [['payments'], ['customer']],
  product: [['shop']],
  outlet: [['catalog'], ['shop-profile'], ['admin', 'outlets'], ['pos', 'config'], ['outlets']],
  payment: [['activities'], ['customer']],
  finance_entry: [['shop']],
  daily_closing: [['shop']],
  turf_booking: MONEY,
  game_play: MONEY,
  event_ticket: MONEY,
  kart_session: MONEY,
  turf_package: MONEY,
  user: [['admin', 'users'], ['shop-users']],
  feedback: [['shop']],
  settings: [['admin', 'settings']],
};

/** The react-query key prefixes to refresh for one event (deduplicated). */
export function keysFor(ev: Pick<LiveEvent, 'entity' | 'action'>): QueryKey[] {
  const all = [...BY_AREA[areaOf(ev)], ...(BY_ENTITY[ev.entity] ?? [])];
  if (ev.action.startsWith('wallet.')) all.push(...CARDS);
  const seen = new Set<string>();
  return all.filter((k) => {
    const s = JSON.stringify(k);
    if (seen.has(s)) return false;
    seen.add(s);
    return true;
  });
}

/** Every prefix any event can refresh: used to catch up after the stream was down or the tab was hidden. */
export function allLiveKeys(): QueryKey[] {
  const seen = new Set<string>();
  return [...Object.values(BY_AREA).flat(), ...Object.values(BY_ENTITY).flat()].filter((k) => {
    const s = JSON.stringify(k);
    if (seen.has(s)) return false;
    seen.add(s);
    return true;
  });
}

/** Wait before the n-th reconnect attempt (0-based): 1 s, 2 s, 4 s … capped at 30 s, plus a little jitter. */
export function backoffMs(attempt: number, random = Math.random) {
  return Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5)) + Math.floor(random() * 400);
}

// ------------------------------------------------------------------ the one-per-tab stream
const STREAM_URL = '/api/v1/live/stream';
const RECENT_URL = '/api/v1/live/recent';
const MAX_EVENTS = 300;
/** Hidden tabs keep the stream briefly (switching to the printer dialog and back should not reconnect). */
const HIDDEN_GRACE_MS = 20_000;
/** When the server has no stream (404) or refuses it, look again only this often. */
const UNAVAILABLE_RETRY_MS = 5 * 60_000;
const FLUSH_MS = 250;

interface Snapshot {
  status: LiveStatus;
  events: LiveEvent[];
  /** Time (ms) the last event arrived, for the "last update" hint. */
  lastEventAt: number | null;
}

let snap: Snapshot = { status: 'idle', events: [], lastEventAt: null };
const listeners = new Set<() => void>();
const eventListeners = new Set<(ev: LiveEvent) => void>();
const seenIds = new Set<string>();
let users = 0;
let source: EventSource | null = null;
let attempt = 0;
let everOpened = false;
let gap = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const pending = new Map<string, QueryKey>();
let globalsBound = false;
let currentUser: string | null = null;
/** Newest event id received, so a reconnect asks the server to replay what was missed (?since=). */
let lastId: number | null = null;
/** The server ended the stream because the session is over: do not reconnect until someone signs in again. */
let ended = false;

function set(patch: Partial<Snapshot>) {
  snap = { ...snap, ...patch };
  listeners.forEach((l) => l());
}

function queueRefresh(keys: QueryKey[]) {
  for (const k of keys) pending.set(JSON.stringify(k), k);
  if (flushTimer) return;
  // Several events in one burst (a busy hour) become one refresh per screen.
  flushTimer = setTimeout(() => {
    flushTimer = null;
    const keys = [...pending.values()];
    pending.clear();
    for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
  }, FLUSH_MS);
}

function remember(list: LiveEvent[]) {
  for (const e of list) {
    const n = Number(e.id);
    if (Number.isFinite(n) && (lastId === null || n > lastId)) lastId = n;
  }
  const fresh = list.filter((e) => !seenIds.has(e.id));
  if (!fresh.length) return fresh;
  for (const e of fresh) seenIds.add(e.id);
  const events = [...fresh, ...snap.events].sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0)).slice(0, MAX_EVENTS);
  if (seenIds.size > MAX_EVENTS * 4) {
    seenIds.clear();
    for (const e of events) seenIds.add(e.id);
  }
  set({ events });
  return fresh;
}

function receive(data: string) {
  let raw: unknown;
  try {
    raw = JSON.parse(data);
  } catch {
    return;
  }
  const ev = parseLiveEvent(raw);
  if (!ev) return;
  const fresh = remember([ev]);
  if (!fresh.length) return;
  set({ lastEventAt: Date.now() });
  queueRefresh(keysFor(ev));
  eventListeners.forEach((l) => l(ev));
}

/** Loads the latest events (the feed's first screenful, and whatever happened while the stream was down). */
async function backfill() {
  try {
    const res = await fetch(`${RECENT_URL}?limit=50`, { credentials: 'include' });
    if (!res.ok) return;
    const body = (await res.json()) as { items?: unknown[] } | unknown[];
    const items = (Array.isArray(body) ? body : (body.items ?? [])).map(parseLiveEvent).filter((e): e is LiveEvent => !!e);
    remember(items);
  } catch {
    /* offline: the stream will catch up */
  }
}

function clearTimers() {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
}

function close() {
  if (source) {
    source.onopen = source.onmessage = source.onerror = null;
    source.close();
    source = null;
  }
}

function connect() {
  clearTimers();
  if (source || users === 0 || ended || typeof EventSource === 'undefined') return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    set({ status: 'paused' });
    return;
  }
  set({ status: snap.status === 'live' || everOpened ? 'reconnecting' : 'connecting' });
  // After a gap, ask the server to replay what this tab missed (it filters the replay for this user too).
  const es = new EventSource(gap && lastId !== null ? `${STREAM_URL}?since=${lastId}` : STREAM_URL, { withCredentials: true });
  source = es;
  es.onopen = () => {
    attempt = 0;
    const firstTime = !everOpened;
    everOpened = true;
    set({ status: 'live' });
    // After a gap the screens may have missed changes: refresh them once and pull the missed feed rows.
    if (gap && !firstTime) queueRefresh(allLiveKeys());
    gap = false;
    void backfill();
  };
  es.onmessage = (m) => receive(m.data);
  // Servers may name the event type; listen for the likely names as well as plain messages.
  es.addEventListener('activity', (m) => receive((m as MessageEvent).data));
  es.addEventListener('audit', (m) => receive((m as MessageEvent).data));
  // The server ends the stream when the session is over (signed out, expired, login switched off) or it is
  // restarting. Signed out: stay quiet (the next screen load sends the person to sign in); otherwise reconnect.
  es.addEventListener('end', (m) => {
    let reason = '';
    try {
      reason = String((JSON.parse((m as MessageEvent).data) as { reason?: string }).reason ?? '');
    } catch {
      /* no reason given */
    }
    close();
    gap = true;
    if (reason === 'signed_out') {
      ended = true;
      set({ status: 'unavailable' });
      void queryClient.invalidateQueries({ queryKey: ['staff-me'] });
      return;
    }
    void retryLater();
  });
  es.onerror = () => {
    close();
    gap = true;
    void retryLater();
  };
}

async function retryLater() {
  if (users === 0) return;
  // Find out whether the stream exists at all before hammering it. A 404 (no stream on this server) or a
  // refusal means: stay quiet, let every screen poll as before, and look again much later.
  let delay = backoffMs(attempt++);
  try {
    const res = await fetch(`${RECENT_URL}?limit=1`, { credentials: 'include' });
    if (res.status === 404 || res.status === 401 || res.status === 403) {
      set({ status: 'unavailable' });
      delay = UNAVAILABLE_RETRY_MS;
    } else set({ status: 'reconnecting' });
  } catch {
    set({ status: 'reconnecting' });
  }
  if (users === 0 || source) return;
  clearTimers();
  reconnectTimer = setTimeout(connect, delay);
}

function onVisibility() {
  if (document.visibilityState === 'hidden') {
    if (hiddenTimer) return;
    hiddenTimer = setTimeout(() => {
      hiddenTimer = null;
      if (document.visibilityState !== 'hidden') return;
      close();
      clearTimers();
      gap = true;
      set({ status: 'paused' });
    }, HIDDEN_GRACE_MS);
  } else {
    if (hiddenTimer) clearTimeout(hiddenTimer);
    hiddenTimer = null;
    if (!source && users > 0) {
      attempt = 0;
      connect();
    }
  }
}

function onOnline() {
  if (!source && users > 0) {
    attempt = 0;
    connect();
  }
}

function bindGlobals() {
  if (globalsBound || typeof window === 'undefined') return;
  globalsBound = true;
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', onOnline);
}

function retain() {
  users++;
  bindGlobals();
  if (users === 1) {
    void backfill();
    connect();
  }
}

function release() {
  users = Math.max(0, users - 1);
  if (users > 0) return;
  // Leave the stream open a moment: moving between screens unmounts one user just before the next mounts.
  setTimeout(() => {
    if (users > 0) return;
    close();
    clearTimers();
    set({ status: 'idle' });
  }, 1500);
}

/** Forget everything (sign-out): the next user of this tab must not see the previous user's feed. */
export function resetLive() {
  close();
  clearTimers();
  seenIds.clear();
  everOpened = false;
  attempt = 0;
  lastId = null;
  ended = false;
  set({ status: users > 0 ? 'connecting' : 'idle', events: [], lastEventAt: null });
  if (users > 0) {
    void backfill();
    connect();
  }
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const getSnapshot = () => snap;

/**
 * Keeps this tab's live stream open while the calling component is mounted and returns its state:
 * connection status and the latest events (newest first). Mount it anywhere; all users share one stream.
 */
export function useLiveEvents(userId?: string): Snapshot {
  useEffect(() => {
    // A different person signed in on this tab: drop the previous person's feed before showing anything.
    if (userId && userId !== currentUser) {
      if (currentUser) resetLive();
      currentUser = userId;
    } else if (userId && ended) {
      // The same person signed in again after the server ended their stream.
      resetLive();
    }
  }, [userId]);
  useEffect(() => {
    retain();
    return release;
  }, []);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Calls `onEvent` for each new event while mounted (e.g. to flash a tile or bump a counter). */
export function useLiveEvent(onEvent: (ev: LiveEvent) => void) {
  const ref = useRef(onEvent);
  ref.current = onEvent;
  useEffect(() => {
    const l = (ev: LiveEvent) => ref.current(ev);
    eventListeners.add(l);
    return () => {
      eventListeners.delete(l);
    };
  }, []);
}

/** Plain-language status line for the indicator's tooltip and screen readers. */
export function statusText(status: LiveStatus) {
  switch (status) {
    case 'live':
      return 'Live: screens update by themselves';
    case 'connecting':
      return 'Connecting to live updates…';
    case 'reconnecting':
      return 'Reconnecting… screens still refresh every few seconds';
    case 'paused':
      return 'Paused while this tab is in the background';
    case 'unavailable':
      return 'Live updates are off on this server; screens refresh every few seconds';
    default:
      return 'Live updates are off';
  }
}
